import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { PageHeader } from '@/components/AIBadge';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    Search, ChevronDown, ChevronRight, Plus, Save, Trash2, X, Edit3, Loader2, ClipboardList,
    Building2, Globe2, Tag, Sparkles, AlertTriangle,
} from 'lucide-react';
import { INDUSTRIES as FALLBACK_INDUSTRIES } from '@/constants/industries';
import { EnrichWithAIModal, ReviewSuggestionsModal, RecentChangesWidget } from './ChecklistAIEnrichment';

const ACCENT = '#7C6BEF';
const WEIGHT_LABEL = { 1: 'Estándar', 2: 'NOM', 3: 'Crítico' };

// Iter 106 · Detecta SOLO citas realmente corruptas (placeholder/vacío-basura).
// Regla nueva: una cita es VÁLIDA si contiene cualquier código de norma o
// referencia reconocible, sin importar el numeral/cláusula/detalle que traiga.
// Solo se marca inválida si es un marcador vacío tipo "None"/"N/A".
const CITATION_PLACEHOLDERS = ['none', 'n/a', 'na', 'n.a.', 'null', '-', 's/n', 'sin', 'general', '.'];
function isCitationInvalid(sr) {
    const raw = (sr || '').trim();
    if (!raw) return false; // sin cita = no marcamos
    return CITATION_PLACEHOLDERS.includes(raw.replace(/[.\s]+$/, '').toLowerCase());
}
const WEIGHT_COLOR = { 1: '#9CA3AF', 2: '#3B82F6', 3: '#EF4444' };

export default function AdminChecklistMaster() {
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [openSection, setOpenSection] = useState(null);
    const [editingItem, setEditingItem] = useState(null); // {id, question, weight, standard_reference, applies_to_industries}
    const [addingTo, setAddingTo] = useState(null); // section_id
    const [editingSection, setEditingSection] = useState(null); // {index, section}
    const [editingField, setEditingField] = useState(null); // {key, ...}
    const [creatingField, setCreatingField] = useState(false); // Iter 47.5 · nuevo campo modal
    const [deletingField, setDeletingField] = useState(null); // Iter 47.5 · {field, usage}
    const [tab, setTab] = useState('sections'); // 'sections' | 'general'
    const [industryOptions, setIndustryOptions] = useState(FALLBACK_INDUSTRIES);
    // Iter 54 · Fase 3 · AI enrichment state
    const [enrichSection, setEnrichSection] = useState(null); // section object → open EnrichWithAIModal
    const [reviewSection, setReviewSection] = useState(null); // {section, batchId?} → open ReviewSuggestionsModal
    const [pendingCounts, setPendingCounts] = useState({}); // { section_id: N pending_review }
    const [changesRefreshKey, setChangesRefreshKey] = useState(0);

    const load = () => {
        setLoading(true);
        api.get('/checklist-template')
            .then((r) => setTemplate(r.data))
            .catch((e) => toast.error(apiErrorMessage(e, 'Error al cargar')))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);
    useEffect(() => {
        api.get('/distintivos').then((r) => setDistintivos(r.data || [])).catch(() => {});
    }, []);

    // Load rubro names as primary industry options; fallback to constant.
    useEffect(() => {
        api.get('/rubros')
            .then((r) => {
                const names = (r.data || []).map((x) => x.name).filter(Boolean).sort();
                if (names.length > 0) setIndustryOptions(names);
            })
            .catch(() => { /* keep fallback */ });
    }, []);

    const [distintivos, setDistintivos] = useState([]);

    // Iter 54 · Load pending suggestion counts by section for the badge
    const loadPendingCounts = () => {
        api.get('/admin/checklist-master/ai-suggestions?status=pending_review&limit=500')
            .then((r) => {
                const counts = {};
                for (const s of (r.data || [])) {
                    const sid = s.target_section_id;
                    if (sid) counts[sid] = (counts[sid] || 0) + 1;
                }
                setPendingCounts(counts);
            })
            .catch(() => setPendingCounts({}));
    };
    useEffect(() => { loadPendingCounts(); }, [changesRefreshKey]);

    const saveSection = async () => {
        try {
            await api.patch(`/admin/checklist-template/sections/${editingSection.index}`, {
                applies_to_industries: editingSection.section.applies_to_industries || [],
                distintivos_incluidos: editingSection.section.distintivos_incluidos || [],
            });
            toast.success('Sección actualizada');
            setEditingSection(null);
            await load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al guardar sección')); }
    };

    const saveGeneralField = async () => {
        try {
            const payload = {
                applies_to_industries: editingField.applies_to_industries || [],
            };
            if (editingField.label !== undefined) {
                payload.label = (editingField.label || '').trim();
            }
            if (editingField.type !== undefined) payload.type = editingField.type;
            if (editingField.options !== undefined) payload.options = editingField.options;
            if (editingField.unit !== undefined) payload.unit = editingField.unit;
            await api.patch(`/admin/checklist-template/general-fields/${editingField.key}`, payload);
            toast.success('Campo actualizado');
            setEditingField(null);
            await load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al guardar campo')); }
    };

    const createGeneralField = async (data) => {
        try {
            await api.post('/admin/checklist-template/general-fields', data);
            toast.success('Campo creado');
            setCreatingField(false);
            await load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al crear campo')); throw e; }
    };

    const openDeleteField = async (field) => {
        try {
            const r = await api.get(`/admin/checklist-template/general-fields/${field.key}/usage`);
            setDeletingField({ field, usage: r.data });
            setEditingField(null); // close editor if open
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al obtener uso del campo')); }
    };

    const confirmDeleteField = async () => {
        try {
            const r = await api.delete(`/admin/checklist-template/general-fields/${deletingField.field.key}`);
            toast.success(`Campo eliminado · ${r.data.responses_archived || 0} respuestas archivadas`);
            setDeletingField(null);
            await load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al eliminar campo')); }
    };

    const totals = useMemo(() => {
        if (!template) return { sections: 0, items: 0 };
        const sections = template.sections?.length || 0;
        const items = (template.sections || []).reduce((acc, s) => acc + (s.items?.length || 0), 0);
        return { sections, items };
    }, [template]);

    const filteredSections = useMemo(() => {
        if (!template) return [];
        const query = q.trim().toLowerCase();        if (!query) return template.sections || [];
        return (template.sections || [])
            .map((s) => ({
                ...s,
                items: (s.items || []).filter((it) =>
                    [it.id, it.question, it.standard_reference].join(' ').toLowerCase().includes(query)
                ),
            }))
            .filter((s) => s.items.length > 0 || s.title.toLowerCase().includes(query) || s.section_id.toLowerCase().includes(query));
    }, [q, template]);

    const filteredGeneralFields = useMemo(() => {
        if (!template) return [];
        const query = q.trim().toLowerCase();
        const list = template.general_data_fields || [];
        if (!query) return list;
        return list.filter((f) =>
            [f.key, f.label, f.type].filter(Boolean).join(' ').toLowerCase().includes(query)
        );
    }, [q, template]);

    const saveEdit = async () => {
        try {
            await api.patch(`/admin/checklist-template/items/${editingItem.id}`, {
                question: editingItem.question,
                weight: editingItem.weight,
                standard_reference: editingItem.standard_reference,
                applies_to_industries: editingItem.applies_to_industries || [],
                distintivos_incluidos: editingItem.distintivos_incluidos || [],
                blocking: !!editingItem.blocking,
            });
            toast.success('Ítem actualizado');
            setEditingItem(null);
            load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al actualizar')); }
    };

    const deleteItem = async (itemId) => {
        if (!window.confirm('¿Eliminar este ítem del master checklist?')) return;
        try {
            await api.delete(`/admin/checklist-template/items/${itemId}`);
            toast.success('Ítem eliminado');
            load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al eliminar')); }
    };

    const addItem = async (sectionId, data) => {
        try {
            await api.post(`/admin/checklist-template/sections/${sectionId}/items`, data);
            toast.success('Ítem creado');
            setAddingTo(null);
            load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al crear')); }
    };

    return (
        <AdminLayout title="Editor del Master Checklist">
            <PageHeader
                title="Master Checklist — Editor"
                subtitle={`◆ ${totals.sections} secciones · ${totals.items} ítems totales`}
                accent={ACCENT}
            />

            {/* Iter 54 · Widget: Últimos cambios al master (colapsable) */}
            <RecentChangesWidget key={`chg-${changesRefreshKey}`} />

            {/* Tabs */}
            <div className="mb-4 flex gap-1 border-b" style={{ borderColor: 'var(--border)' }}>
                <button
                    type="button"
                    onClick={() => setTab('sections')}
                    data-testid="master-tab-sections"
                    className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${tab === 'sections' ? '' : 'opacity-60 hover:opacity-90'}`}
                    style={{ borderColor: tab === 'sections' ? ACCENT : 'transparent', color: tab === 'sections' ? ACCENT : 'inherit' }}
                >
                    <ClipboardList className="w-3.5 h-3.5 inline mr-1.5" /> Secciones e ítems
                </button>
                <button
                    type="button"
                    onClick={() => setTab('general')}
                    data-testid="master-tab-general"
                    className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${tab === 'general' ? '' : 'opacity-60 hover:opacity-90'}`}
                    style={{ borderColor: tab === 'general' ? ACCENT : 'transparent', color: tab === 'general' ? ACCENT : 'inherit' }}
                >
                    <Building2 className="w-3.5 h-3.5 inline mr-1.5" /> Datos de Relevamiento ({template?.general_data_fields?.length || 0})
                </button>
            </div>

            <div className="mb-6 flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 px-4 py-3 border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
                    <Search className="w-4 h-4 opacity-60" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={tab === 'sections' ? 'Buscar ítem por id, pregunta o norma...' : 'Buscar campo por clave o etiqueta...'}
                        data-testid="master-search"
                        className="flex-1 bg-transparent border-0 outline-none text-sm"
                    />
                </div>
                <div className="text-xs uppercase tracking-widest font-bold px-4 py-3 border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
                    {tab === 'sections'
                        ? `${filteredSections.reduce((a, s) => a + s.items.length, 0)} coincidencias`
                        : `${filteredGeneralFields.length} campos`}
                </div>
            </div>

            {loading ? (
                <div className="p-12 text-center opacity-60">Cargando master checklist...</div>
            ) : tab === 'sections' ? (
                <div className="space-y-3" data-testid="master-sections">
                    {filteredSections.map((sec) => (
                        <SectionAccordion
                            key={sec.section_id}
                            section={sec}
                            index={template.sections.indexOf(sec)}
                            open={openSection === sec.section_id || !!q.trim()}
                            onToggle={() => setOpenSection(openSection === sec.section_id ? null : sec.section_id)}
                            onEdit={(it) => setEditingItem({ ...it })}
                            onEditSection={(index, section) => setEditingSection({ index, section: { ...section } })}
                            onDelete={deleteItem}
                            onAdd={() => setAddingTo(sec.section_id)}
                            onEnrichAI={() => setEnrichSection(sec)}
                            onReviewAI={() => setReviewSection({ section: sec })}
                            pendingCount={pendingCounts[sec.section_id] || 0}
                        />
                    ))}
                    {filteredSections.length === 0 && (
                        <div className="p-12 text-center opacity-60" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                            Sin resultados
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <div className="flex justify-end mb-3">
                        <button type="button" onClick={() => setCreatingField(true)}
                            data-testid="new-general-field-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-bold"
                            style={{ background: ACCENT }}>
                            <Plus className="w-4 h-4" /> Nuevo campo
                        </button>
                    </div>
                    <GeneralFieldsList
                        fields={filteredGeneralFields}
                        onEdit={(f) => setEditingField({ ...f, _originalType: f.type })}
                    />
                </>
            )}

            {editingItem && (
                <ItemEditor
                    item={editingItem}
                    industryOptions={industryOptions}
                    distintivos={distintivos}
                    parentSection={(template?.sections || []).find((s) => (s.items || []).some((it) => it.id === editingItem.id))}
                    onChange={(patch) => setEditingItem({ ...editingItem, ...patch })}
                    onSave={saveEdit}
                    onClose={() => setEditingItem(null)}
                />
            )}

            {editingSection && (
                <SectionEditor
                    section={editingSection.section}
                    industryOptions={industryOptions}
                    distintivos={distintivos}
                    onChange={(patch) => setEditingSection({ ...editingSection, section: { ...editingSection.section, ...patch } })}
                    onSave={saveSection}
                    onClose={() => setEditingSection(null)}
                />
            )}

            {editingField && (
                <GeneralFieldEditor
                    field={editingField}
                    industryOptions={industryOptions}
                    onChange={(patch) => setEditingField({ ...editingField, ...patch })}
                    onSave={saveGeneralField}
                    onDelete={() => openDeleteField(editingField)}
                    onClose={() => setEditingField(null)}
                />
            )}

            {creatingField && (
                <NewGeneralFieldForm
                    industryOptions={industryOptions}
                    onCreate={createGeneralField}
                    onClose={() => setCreatingField(false)}
                />
            )}

            {deletingField && (
                <DeleteFieldConfirm
                    field={deletingField.field}
                    usage={deletingField.usage}
                    onConfirm={confirmDeleteField}
                    onClose={() => setDeletingField(null)}
                />
            )}

            {addingTo && (
                <ItemCreator
                    sectionId={addingTo}
                    onSave={(data) => addItem(addingTo, data)}
                    onClose={() => setAddingTo(null)}
                />
            )}

            {/* Iter 54 · Fase 3 · AI enrichment modals */}
            {enrichSection && (
                <EnrichWithAIModal
                    section={enrichSection}
                    onClose={() => setEnrichSection(null)}
                    onGenerated={(data) => {
                        // Auto-open review modal filtered to this batch
                        setEnrichSection(null);
                        setReviewSection({ section: enrichSection, batchId: data.batch_id });
                        loadPendingCounts();
                    }}
                />
            )}
            {reviewSection && (
                <ReviewSuggestionsModal
                    section={reviewSection.section}
                    batchId={reviewSection.batchId}
                    allSections={(template?.sections || []).map((s) => ({ section_id: s.section_id, title: s.title, section_title: s.section_title }))}
                    onClose={() => { setReviewSection(null); loadPendingCounts(); }}
                    onAppliedRefresh={() => {
                        load();
                        setChangesRefreshKey((v) => v + 1);
                    }}
                />
            )}
        </AdminLayout>
    );
}

function SectionAccordion({ section, index, open, onToggle, onEdit, onEditSection, onDelete, onAdd, onEnrichAI, onReviewAI, pendingCount = 0 }) {
    const industries = section.applies_to_industries || [];
    return (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid={`section-${section.section_id}`}>
            <div className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-black/[0.02] dark:hover:bg-white/5">
                <button type="button" onClick={onToggle} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                    {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <ClipboardList className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
                    <span className="font-display font-bold text-sm truncate">
                        <span style={{ color: ACCENT }}>{section.section_id}</span> {section.title}
                    </span>
                </button>
                <IndustryPill industries={industries} testId={`section-industries-${section.section_id}`} />
                {pendingCount > 0 && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onReviewAI && onReviewAI(); }}
                        data-testid={`review-ai-${section.section_id}`}
                        title={`${pendingCount} propuestas IA pendientes de revisión`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold shrink-0"
                        style={{ background: `${ACCENT}22`, color: ACCENT }}
                    >
                        <Sparkles className="w-3 h-3" /> {pendingCount} pendientes
                    </button>
                )}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onEnrichAI && onEnrichAI(); }}
                    data-testid={`enrich-ai-${section.section_id}`}
                    className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 shrink-0"
                    title="Enriquecer sección con IA (Tavily + Gemini)"
                >
                    <Sparkles className="w-3.5 h-3.5" style={{ color: ACCENT }} />
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onEditSection(index, section); }}
                    data-testid={`edit-section-${section.section_id}`}
                    className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 shrink-0"
                    title="Editar giros aplicables a esta sección"
                >
                    <Tag className="w-3.5 h-3.5" style={{ color: ACCENT }} />
                </button>
                <span className="text-xs opacity-60 shrink-0">{section.items?.length || 0} ítems</span>
            </div>
            {open && (
                <div className="px-5 pb-5 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
                    <button onClick={onAdd}
                        data-testid={`add-item-${section.section_id}`}
                        className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-white"
                        style={{ background: ACCENT }}>
                        <Plus className="w-3.5 h-3.5" /> Agregar ítem
                    </button>
                    <div className="space-y-2">
                        {section.items?.map((it) => (
                            <div key={it.id} className="border px-3 py-3 flex items-start gap-3" style={{ borderColor: 'var(--border)' }} data-testid={`item-${it.id}`}>
                                <span className="text-[10px] font-mono px-2 py-0.5 shrink-0" style={{ background: 'rgba(124,107,239,0.15)', color: ACCENT }}>{it.id}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm">{it.question}</p>
                                    {it.standard_reference && (
                                        <div className="text-[11px] opacity-60 font-mono mt-1 flex items-center gap-2 flex-wrap">
                                            <span>📜 {it.standard_reference}</span>
                                            {isCitationInvalid(it.standard_reference) && (
                                                <span
                                                    data-testid={`item-invalid-citation-${it.id}`}
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full not-italic"
                                                    style={{ background: 'rgba(220,38,38,0.12)', color: '#DC2626' }}
                                                    title="Cita normativa corrupta o mal formada. Edita el ítem y corrige la referencia."
                                                >
                                                    <AlertTriangle className="w-3 h-3" /> Cita inválida
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <IndustryPill industries={it.applies_to_industries || []} testId={`item-industries-${it.id}`} compact />
                                </div>
                                {/* Iter 60 · override badge — visible when item carries its own distintivos_incluidos */}
                                {(it.distintivos_incluidos || []).length > 0 && (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 shrink-0"
                                        style={{ background: `${ACCENT}22`, color: ACCENT, border: `1px solid ${ACCENT}55` }}
                                        title={`Override activo: ${(it.distintivos_incluidos || []).length} distintivo(s) explícitos (no hereda de la sección)`}
                                        data-testid={`item-override-badge-${it.id}`}
                                    >
                                        <Tag className="w-3 h-3" />
                                        override · {(it.distintivos_incluidos || []).length}
                                    </span>
                                )}
                                {/* Iter 62 · Fase 3 · blocking hard-stop badge */}
                                {it.blocking && (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 shrink-0"
                                        style={{ background: 'rgba(220,38,38,0.15)', color: '#DC2626', border: '1px solid rgba(220,38,38,0.45)' }}
                                        title="Ítem bloqueante: un 'No cumple' fuerza REPROBADO en la auditoría, sin importar el score"
                                        data-testid={`item-blocking-badge-${it.id}`}
                                    >
                                        <AlertTriangle className="w-3 h-3" />
                                        bloqueante
                                    </span>
                                )}
                                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 shrink-0"
                                    style={{ background: `${WEIGHT_COLOR[it.weight]}22`, color: WEIGHT_COLOR[it.weight] }}>
                                    Peso {it.weight} · {WEIGHT_LABEL[it.weight]}
                                </span>
                                <button onClick={() => onEdit(it)} data-testid={`edit-${it.id}`}
                                    className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10">
                                    <Edit3 className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => onDelete(it.id)} data-testid={`delete-${it.id}`}
                                    className="p-1.5 hover:bg-red-500/10 text-red-500">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ItemEditor({ item, industryOptions, distintivos = [], parentSection, onChange, onSave, onClose }) {
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try { await onSave(); }
        finally { setBusy(false); }
    };
    // Iter 60 · item-level distintivo tagging
    const itemDistintivos = item.distintivos_incluidos || [];
    const sectionDistintivos = (parentSection?.distintivos_incluidos) || [];
    const isOverride = itemDistintivos.length > 0;
    const effectiveIds = isOverride ? itemDistintivos : sectionDistintivos;
    const distintivoById = useMemo(() => {
        const m = {};
        for (const d of distintivos) m[d.id] = d;
        return m;
    }, [distintivos]);
    const toggleDistintivo = (id) => {
        const cur = itemDistintivos;
        const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
        onChange({ distintivos_incluidos: next });
    };
    const clearOverride = () => onChange({ distintivos_incluidos: [] });
    return (
        <Modal onClose={onClose} title={`Editar ítem · ${item.id}`}>
            <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 block mb-1.5">Pregunta</label>
            <textarea
                value={item.question}
                onChange={(e) => onChange({ question: e.target.value })}
                rows={3}
                data-testid="edit-question"
                className="w-full px-3 py-2 border outline-none text-sm mb-4 resize-none bg-transparent"
                style={{ borderColor: 'var(--border)' }}
            />
            <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 block mb-1.5">Referencia normativa</label>
            <input
                value={item.standard_reference || ''}
                onChange={(e) => onChange({ standard_reference: e.target.value })}
                placeholder="ej. NOM-002-STPS-2010"
                data-testid="edit-reference"
                className="w-full px-3 py-2 border outline-none text-sm mb-4 bg-transparent"
                style={{ borderColor: 'var(--border)' }}
            />
            <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 block mb-1.5">Peso</label>
            <div className="flex gap-2 mb-6">
                {[1, 2, 3].map((w) => (
                    <button key={w} onClick={() => onChange({ weight: w })}
                        data-testid={`edit-weight-${w}`}
                        className="px-4 py-2 border text-xs font-bold"
                        style={{
                            borderColor: item.weight === w ? WEIGHT_COLOR[w] : 'var(--border)',
                            background: item.weight === w ? `${WEIGHT_COLOR[w]}22` : 'transparent',
                            color: item.weight === w ? WEIGHT_COLOR[w] : 'inherit',
                        }}>
                        {w} · {WEIGHT_LABEL[w]}
                    </button>
                ))}
            </div>

            {/* Iter 62 · Fase 3 · Bloqueante (hard-stop) toggle */}
            <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 block mb-1.5">Ítem bloqueante</label>
            <button
                type="button"
                onClick={() => onChange({ blocking: !item.blocking })}
                data-testid="edit-blocking-toggle"
                className="w-full flex items-start gap-3 px-3 py-3 border text-left mb-6 hover:bg-black/[0.02] dark:hover:bg-white/5"
                style={{
                    borderColor: item.blocking ? '#DC2626' : 'var(--border)',
                    background: item.blocking ? 'rgba(220,38,38,0.06)' : 'transparent',
                }}
            >
                <span
                    className="mt-0.5 shrink-0 w-4 h-4 border flex items-center justify-center"
                    style={{
                        borderColor: item.blocking ? '#DC2626' : 'var(--border)',
                        background: item.blocking ? '#DC2626' : 'transparent',
                    }}
                    aria-hidden
                >
                    {item.blocking && <span className="w-2 h-2 bg-white" />}
                </span>
                <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-bold">
                        <AlertTriangle className="w-3.5 h-3.5" style={{ color: item.blocking ? '#DC2626' : '#9CA3AF' }} />
                        Un &quot;No cumple&quot; en este ítem fuerza REPROBADO
                    </span>
                    <span className="text-[11px] opacity-70 block mt-1">
                        Independiente del score porcentual. Úsalo solo para riesgos críticos verificados por norma
                        (ej. sistemas contra incendio inoperantes, gas LP sin válvula de corte, salidas de emergencia bloqueadas).
                    </span>
                </span>
            </button>
            <IndustryPicker
                label="Giros aplicables"
                value={item.applies_to_industries || []}
                options={industryOptions}
                onChange={(v) => onChange({ applies_to_industries: v })}
                testId="edit-industries"
            />
            {/* Iter 60 · item-level distintivo tagging (override vs. inherit) */}
            <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs uppercase tracking-wider font-bold text-gray-500">
                        Distintivos incluidos {isOverride
                            ? <span style={{ color: ACCENT }}>({itemDistintivos.length} · override)</span>
                            : <span className="opacity-60">(heredado de sección · {sectionDistintivos.length})</span>}
                    </label>
                    {isOverride && (
                        <button
                            type="button"
                            onClick={clearOverride}
                            data-testid="item-distintivos-clear-override"
                            className="text-[10px] uppercase tracking-wider font-bold opacity-70 hover:opacity-100 border px-2 py-1"
                            style={{ borderColor: 'var(--border)' }}
                            title="Volver a heredar de la sección"
                        >
                            Heredar de sección
                        </button>
                    )}
                </div>
                {distintivos.length === 0 ? (
                    <div className="text-xs italic opacity-60 border px-3 py-2" style={{ borderColor: 'var(--border)' }}>
                        Aún no hay distintivos definidos. Créalos en /admin/distintivos.
                    </div>
                ) : (
                    <div className="border max-h-40 overflow-y-auto" style={{ borderColor: 'var(--border)' }} data-testid="item-distintivos-picker">
                        {distintivos.map((d) => {
                            const inheritedActive = !isOverride && sectionDistintivos.includes(d.id);
                            const overrideActive = isOverride && itemDistintivos.includes(d.id);
                            const checked = overrideActive || inheritedActive;
                            return (
                                <label key={d.id} className="flex items-center gap-2 px-3 py-1.5 border-b last:border-b-0 text-sm cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/5" style={{ borderColor: 'var(--border)' }}>
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleDistintivo(d.id)}
                                        data-testid={`item-distintivo-${d.code}`}
                                    />
                                    <span className="font-mono text-xs opacity-60">{d.code}</span>
                                    <span className="flex-1">{d.name}</span>
                                    {inheritedActive && !isOverride && (
                                        <span className="text-[9px] uppercase tracking-wider font-bold opacity-60" title="Heredado de la sección">
                                            heredado
                                        </span>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                )}
                <p className="text-[11px] opacity-60 mt-1.5">
                    Marca uno o más distintivos para <span className="font-bold">sobrescribir</span> la herencia de la sección. Con la lista vacía, el ítem hereda los distintivos de su sección padre.
                    {effectiveIds.length > 0 && (
                        <span className="block mt-1">
                            Efectivo: {effectiveIds.map((id) => distintivoById[id]?.code || '?').join(', ')}
                        </span>
                    )}
                </p>
            </div>
            <div className="flex gap-2 pt-4 border-t sticky bottom-0 -mx-5 -mb-5 px-5 py-3" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                <button onClick={submit} disabled={busy}
                    data-testid="edit-save"
                    className="flex-1 text-white px-5 py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: ACCENT }}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar cambios
                </button>
                <button onClick={onClose} disabled={busy}
                    data-testid="edit-cancel"
                    className="border-2 px-5 py-2.5 font-bold hover:bg-black/5 dark:hover:bg-white/5"
                    style={{ borderColor: 'var(--border)' }}>
                    Cancelar
                </button>
            </div>
        </Modal>
    );
}

function ItemCreator({ sectionId, onSave, onClose }) {
    const [data, setData] = useState({ question: '', standard_reference: '', weight: 2 });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!data.question.trim()) { toast.error('La pregunta es obligatoria'); return; }
        setBusy(true);
        try { await onSave(data); }
        finally { setBusy(false); }
    };
    return (
        <Modal onClose={onClose} title={`Nuevo ítem · Sección ${sectionId}`}>
            <form onSubmit={submit}>
                <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 block mb-1.5">Pregunta *</label>
                <textarea
                    value={data.question}
                    onChange={(e) => setData({ ...data, question: e.target.value })}
                    rows={3}
                    required
                    data-testid="add-question"
                    placeholder="¿La empresa cuenta con...?"
                    className="w-full px-3 py-2 border outline-none text-sm mb-4 resize-none bg-transparent"
                    style={{ borderColor: 'var(--border)' }}
                />
                <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 block mb-1.5">Referencia normativa</label>
                <input
                    value={data.standard_reference}
                    onChange={(e) => setData({ ...data, standard_reference: e.target.value })}
                    placeholder="ej. NOM-002-STPS-2010"
                    data-testid="add-reference"
                    className="w-full px-3 py-2 border outline-none text-sm mb-4 bg-transparent"
                    style={{ borderColor: 'var(--border)' }}
                />
                <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 block mb-1.5">Peso</label>
                <div className="flex gap-2 mb-6">
                    {[1, 2, 3].map((w) => (
                        <button key={w} type="button" onClick={() => setData({ ...data, weight: w })}
                            data-testid={`add-weight-${w}`}
                            className="px-4 py-2 border text-xs font-bold"
                            style={{
                                borderColor: data.weight === w ? WEIGHT_COLOR[w] : 'var(--border)',
                                background: data.weight === w ? `${WEIGHT_COLOR[w]}22` : 'transparent',
                                color: data.weight === w ? WEIGHT_COLOR[w] : 'inherit',
                            }}>
                            {w} · {WEIGHT_LABEL[w]}
                        </button>
                    ))}
                </div>
                <div className="flex gap-2 pt-4 border-t sticky bottom-0 -mx-5 -mb-5 px-5 py-3" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    <button type="submit" disabled={busy}
                        data-testid="add-save"
                        className="flex-1 text-white px-5 py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        style={{ background: ACCENT }}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Crear ítem
                    </button>
                    <button type="button" onClick={onClose} disabled={busy}
                        data-testid="add-cancel"
                        className="border-2 px-5 py-2.5 font-bold hover:bg-black/5 dark:hover:bg-white/5"
                        style={{ borderColor: 'var(--border)' }}>
                        Cancelar
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function Modal({ children, onClose, title, maxWidth = 'max-w-lg' }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className={`relative w-full ${maxWidth} shadow-2xl flex flex-col max-h-[90vh]`} style={{ background: 'var(--card-bg)' }}>
                <div className="p-5 border-b flex items-center justify-between gap-3 shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <div className="font-display font-black text-lg">{title}</div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/5" data-testid="modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-5 overflow-y-auto flex-1 min-h-0">{children}</div>
            </div>
        </div>
    );
}


// ============ Iter 39 helpers ============

function IndustryPill({ industries, testId, compact = false }) {
    if (!industries || industries.length === 0) {
        return (
            <span
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
                style={{ background: 'rgba(107,114,128,0.12)', color: '#6B7280' }}
                data-testid={testId}
                title="Sin filtro por giro"
            >
                <Globe2 className="w-3 h-3" /> Aplica a todos los giros
            </span>
        );
    }
    const first = industries.slice(0, compact ? 2 : 3);
    const rest = industries.length - first.length;
    return (
        <span
            className="inline-flex items-center gap-1 flex-wrap max-w-[420px] shrink-0"
            data-testid={testId}
            title={industries.join(' · ')}
        >
            {first.map((n) => (
                <span
                    key={n}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(124,107,239,0.12)', color: ACCENT }}
                >
                    <Tag className="w-2.5 h-2.5" /> {n}
                </span>
            ))}
            {rest > 0 && (
                <span className="text-[10px] font-bold opacity-60">+{rest}</span>
            )}
        </span>
    );
}

function IndustryPicker({ label, value, options, onChange, testId }) {
    const [filter, setFilter] = useState('');
    const selected = new Set(value || []);
    const toggle = (n) => {
        const next = new Set(selected);
        if (next.has(n)) next.delete(n); else next.add(n);
        onChange(Array.from(next));
    };
    const filtered = (options || []).filter((n) => !filter.trim() || n.toLowerCase().includes(filter.toLowerCase()));
    return (
        <div className="mb-6" data-testid={testId}>
            <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 block mb-1.5">
                {label} <span className="opacity-70 normal-case font-normal">— vacío = aplica a todos</span>
            </label>
            {selected.size > 0 && (
                <div className="flex flex-wrap gap-1 mb-2" data-testid={`${testId}-selected`}>
                    {Array.from(selected).map((n) => (
                        <span key={n} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full" style={{ background: `${ACCENT}22`, color: ACCENT }}>
                            {n}
                            <button type="button" onClick={() => toggle(n)} data-testid={`${testId}-remove-${n}`} className="hover:opacity-70">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filtrar giros..."
                data-testid={`${testId}-filter`}
                className="w-full px-3 py-2 border outline-none text-sm bg-transparent mb-2"
                style={{ borderColor: 'var(--border)' }}
            />
            <div className="max-h-52 overflow-y-auto border" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                {filtered.length === 0 ? (
                    <div className="p-3 text-xs opacity-60 italic">Sin coincidencias</div>
                ) : filtered.map((n) => (
                    <label key={n} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-black/5 dark:hover:bg-white/10 border-b last:border-b-0" style={{ borderColor: 'var(--border)' }}>
                        <input
                            type="checkbox"
                            checked={selected.has(n)}
                            onChange={() => toggle(n)}
                            data-testid={`${testId}-opt-${n}`}
                            className="accent-[#7C6BEF]"
                        />
                        {n}
                    </label>
                ))}
            </div>
        </div>
    );
}

function SectionEditor({ section, industryOptions, distintivos, onChange, onSave, onClose }) {
    const [busy, setBusy] = useState(false);
    const submit = async () => { setBusy(true); try { await onSave(); } finally { setBusy(false); } };
    const toggleDistintivo = (id) => {
        const cur = section.distintivos_incluidos || [];
        onChange({ distintivos_incluidos: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
    };
    return (
        <Modal onClose={onClose} title={`Editar sección · ${section.section_id}`}>
            <div className="text-sm opacity-80 mb-4">{section.title}</div>
            <IndustryPicker
                label="Giros aplicables a esta sección"
                value={section.applies_to_industries || []}
                options={industryOptions}
                onChange={(v) => onChange({ applies_to_industries: v })}
                testId="section-industries-picker"
            />
            <div className="mb-4">
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">
                    Distintivos incluidos ({(section.distintivos_incluidos || []).length} / {distintivos.length})
                </label>
                {distintivos.length === 0 ? (
                    <div className="text-xs italic opacity-60 border px-3 py-2" style={{ borderColor: 'var(--border)' }}>
                        Aún no hay distintivos definidos. Créalos en /admin/distintivos.
                    </div>
                ) : (
                    <div className="border max-h-40 overflow-y-auto" style={{ borderColor: 'var(--border)' }} data-testid="section-distintivos-picker">
                        {distintivos.map((d) => (
                            <label key={d.id} className="flex items-center gap-2 px-3 py-1.5 border-b last:border-b-0 text-sm cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/5" style={{ borderColor: 'var(--border)' }}>
                                <input type="checkbox" checked={(section.distintivos_incluidos || []).includes(d.id)} onChange={() => toggleDistintivo(d.id)} data-testid={`section-distintivo-${d.code}`} />
                                <span className="font-mono text-xs opacity-60">{d.code}</span>
                                <span>{d.name}</span>
                            </label>
                        ))}
                    </div>
                )}
            </div>
            <div className="flex gap-2 pt-4 border-t sticky bottom-0 -mx-5 -mb-5 px-5 py-3" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                <button onClick={submit} disabled={busy}
                    data-testid="section-save"
                    className="flex-1 text-white px-5 py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: ACCENT }}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar
                </button>
                <button onClick={onClose} className="px-5 py-2.5 border text-sm font-medium" style={{ borderColor: 'var(--border)' }}>
                    Cancelar
                </button>
            </div>
        </Modal>
    );
}

function GeneralFieldsList({ fields, onEdit }) {
    return (
        <div className="border" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} data-testid="general-fields-list">
            {fields.map((f) => (
                <div key={f.key} className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0" style={{ borderColor: 'var(--border)' }} data-testid={`gfield-${f.key}`}>
                    <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs" style={{ color: ACCENT }}>{f.key}</div>
                        <div className="text-sm truncate">{f.label || <span className="italic opacity-60">(sin etiqueta)</span>}</div>
                        <div className="text-[10px] opacity-60 mt-0.5">tipo: {f.type || 'text'}</div>
                    </div>
                    <IndustryPill industries={f.applies_to_industries || []} testId={`gfield-industries-${f.key}`} compact />
                    <button
                        type="button"
                        onClick={() => onEdit(f)}
                        data-testid={`edit-gfield-${f.key}`}
                        className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10"
                        title="Editar giros"
                    >
                        <Edit3 className="w-3.5 h-3.5" style={{ color: ACCENT }} />
                    </button>
                </div>
            ))}
            {fields.length === 0 && <div className="p-12 text-center opacity-60">Sin campos</div>}
        </div>
    );
}

function GeneralFieldEditor({ field, industryOptions, onChange, onSave, onDelete, onClose }) {
    const [busy, setBusy] = useState(false);
    const submit = async () => { setBusy(true); try { await onSave(); } finally { setBusy(false); } };
    const isSelect = field.type === 'select';
    const isNumber = field.type === 'number';
    const [newOpt, setNewOpt] = useState('');
    const addOpt = () => {
        const v = (newOpt || '').trim();
        if (!v) return;
        const cur = field.options || [];
        if (cur.includes(v)) return;
        onChange({ options: [...cur, v] });
        setNewOpt('');
    };
    const removeOpt = (idx) => {
        const cur = [...(field.options || [])];
        cur.splice(idx, 1);
        onChange({ options: cur });
    };
    const moveOpt = (idx, dir) => {
        const cur = [...(field.options || [])];
        const to = idx + dir;
        if (to < 0 || to >= cur.length) return;
        [cur[idx], cur[to]] = [cur[to], cur[idx]];
        onChange({ options: cur });
    };
    const saveDisabled = busy
        || !(field.label || '').trim()
        || (isSelect && (!field.options || field.options.length === 0));
    return (
        <Modal onClose={onClose} title={`Editar campo · ${field.key}`}>
            <div className="text-[11px] opacity-60 mb-4">
                Clave interna: <span className="font-mono">{field.key}</span>
            </div>
            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">
                Texto de la pregunta (label)
            </label>
            <input type="text" value={field.label || ''}
                onChange={(e) => onChange({ label: e.target.value })}
                data-testid="gfield-label-input"
                placeholder="Ej. ¿Cuenta con escaleras eléctricas?"
                className="w-full px-3 py-2 border-2 outline-none text-sm mb-4"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />

            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">
                Tipo de campo
            </label>
            <select value={field.type || 'text'} onChange={(e) => onChange({ type: e.target.value })}
                data-testid="gfield-type-input"
                className="w-full px-3 py-2 border-2 outline-none text-sm mb-1"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                <option value="text">Texto libre</option>
                <option value="number">Número</option>
                <option value="yesno">Sí / No / N/A</option>
                <option value="select">Selección (opciones)</option>
            </select>
            {field.type !== (field._originalType || field.type) && (
                <div className="text-[11px] text-amber-700 dark:text-amber-500 mb-3 p-2 border-l-2 border-amber-500 bg-amber-50 dark:bg-amber-500/10">
                    ⚠️ Al cambiar el tipo, las respuestas ya capturadas se archivarán automáticamente en <code>general_data_archive</code>.
                </div>
            )}

            {isNumber && (
                <div className="mb-4">
                    <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Unidad (opcional)</label>
                    <input type="text" value={field.unit || ''} onChange={(e) => onChange({ unit: e.target.value })}
                        placeholder="Ej. m² · litros · cantidad"
                        data-testid="gfield-unit-input"
                        className="w-full px-3 py-2 border-2 outline-none text-sm"
                        style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                </div>
            )}

            {isSelect && (
                <div className="mb-4" data-testid="gfield-options-editor">
                    <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Opciones del select</label>
                    <div className="space-y-1 mb-2">
                        {(field.options || []).map((o, idx) => (
                            <div key={`${o}-${idx}`} className="flex items-center gap-2 border px-2 py-1.5" style={{ borderColor: 'var(--border)' }} data-testid={`gfield-option-${idx}`}>
                                <span className="flex-1 text-sm">{o}</span>
                                <button type="button" onClick={() => moveOpt(idx, -1)} className="p-1 hover:bg-black/5" disabled={idx === 0} title="Subir">↑</button>
                                <button type="button" onClick={() => moveOpt(idx, 1)} className="p-1 hover:bg-black/5" disabled={idx === (field.options.length - 1)} title="Bajar">↓</button>
                                <button type="button" onClick={() => removeOpt(idx)} className="p-1 hover:bg-black/5 text-red-600" data-testid={`gfield-remove-option-${idx}`} title="Quitar">×</button>
                            </div>
                        ))}
                        {(field.options || []).length === 0 && (
                            <div className="text-xs italic opacity-60">Agrega al menos una opción</div>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <input type="text" value={newOpt} onChange={(e) => setNewOpt(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOpt())}
                            placeholder="Nueva opción…"
                            data-testid="gfield-new-option-input"
                            className="flex-1 px-3 py-1.5 border-2 outline-none text-sm"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                        <button type="button" onClick={addOpt} data-testid="gfield-add-option-btn"
                            className="px-3 py-1.5 text-white text-xs font-bold" style={{ background: ACCENT }}>
                            Añadir
                        </button>
                    </div>
                </div>
            )}

            <IndustryPicker
                label="Giros aplicables a este campo"
                value={field.applies_to_industries || []}
                options={industryOptions}
                onChange={(v) => onChange({ applies_to_industries: v })}
                testId="gfield-industries-picker"
            />
            <div className="flex gap-2 pt-4 border-t sticky bottom-0 -mx-5 -mb-5 px-5 py-3" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                <button onClick={submit} disabled={saveDisabled}
                    data-testid="gfield-save"
                    className="flex-1 text-white px-5 py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: ACCENT }}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar
                </button>
                {onDelete && (
                    <button onClick={onDelete} data-testid="gfield-delete-btn"
                        className="px-4 py-2.5 border-2 text-sm font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                        style={{ borderColor: '#DC2626' }}>
                        Eliminar
                    </button>
                )}
                <button onClick={onClose} className="px-5 py-2.5 border text-sm font-medium" style={{ borderColor: 'var(--border)' }}>
                    Cancelar
                </button>
            </div>
        </Modal>
    );
}


function NewGeneralFieldForm({ industryOptions, onCreate, onClose }) {
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        key: '', label: '', type: 'text', applies_to_industries: [], options: [], unit: '',
    });
    const [keyEdited, setKeyEdited] = useState(false);
    const [newOpt, setNewOpt] = useState('');

    const setField = (patch) => {
        setForm((prev) => {
            const next = { ...prev, ...patch };
            // Auto-derive key from label unless user manually typed it.
            if (!keyEdited && patch.label !== undefined) {
                next.key = (patch.label || '').toLowerCase()
                    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'field';
            }
            return next;
        });
    };

    const addOpt = () => {
        const v = newOpt.trim();
        if (!v || form.options.includes(v)) return;
        setForm((p) => ({ ...p, options: [...p.options, v] }));
        setNewOpt('');
    };

    const isSelect = form.type === 'select';
    const isNumber = form.type === 'number';
    const disabled = busy
        || !form.label.trim()
        || !form.key.trim()
        || !/^[a-z][a-z0-9_]*$/.test(form.key)
        || (isSelect && form.options.length === 0);

    const submit = async () => {
        setBusy(true);
        try {
            await onCreate({
                key: form.key.trim(),
                label: form.label.trim(),
                type: form.type,
                applies_to_industries: form.applies_to_industries,
                options: isSelect ? form.options : undefined,
                unit: isNumber && form.unit ? form.unit.trim() : undefined,
            });
        } finally { setBusy(false); }
    };

    return (
        <Modal onClose={onClose} title="Nuevo campo · Datos de Relevamiento">
            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Label visible</label>
            <input type="text" value={form.label}
                onChange={(e) => setField({ label: e.target.value })}
                data-testid="new-gfield-label"
                placeholder="Ej. ¿Cuenta con planta de emergencia?"
                className="w-full px-3 py-2 border-2 outline-none text-sm mb-4"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />

            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">
                Clave interna (auto desde el label)
            </label>
            <input type="text" value={form.key}
                onChange={(e) => { setKeyEdited(true); setForm((p) => ({ ...p, key: e.target.value })); }}
                data-testid="new-gfield-key"
                placeholder="ej. planta_emergencia"
                className="w-full px-3 py-2 border-2 outline-none text-sm mb-1 font-mono"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
            <div className="text-[11px] opacity-60 mb-4">Solo minúsculas, dígitos y guión bajo. Debe iniciar con letra.</div>

            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Tipo</label>
            <select value={form.type} onChange={(e) => setField({ type: e.target.value })}
                data-testid="new-gfield-type"
                className="w-full px-3 py-2 border-2 outline-none text-sm mb-4"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                <option value="text">Texto libre</option>
                <option value="number">Número</option>
                <option value="yesno">Sí / No / N/A</option>
                <option value="select">Selección (opciones)</option>
            </select>

            {isNumber && (
                <div className="mb-4">
                    <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Unidad (opcional)</label>
                    <input type="text" value={form.unit} onChange={(e) => setField({ unit: e.target.value })}
                        placeholder="m² · litros · cantidad"
                        data-testid="new-gfield-unit"
                        className="w-full px-3 py-2 border-2 outline-none text-sm"
                        style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                </div>
            )}

            {isSelect && (
                <div className="mb-4">
                    <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Opciones</label>
                    <div className="space-y-1 mb-2">
                        {form.options.map((o, idx) => (
                            <div key={idx} className="flex items-center gap-2 border px-2 py-1.5" style={{ borderColor: 'var(--border)' }}>
                                <span className="flex-1 text-sm">{o}</span>
                                <button type="button" onClick={() => setForm((p) => ({ ...p, options: p.options.filter((_, i) => i !== idx) }))} className="text-red-600">×</button>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input type="text" value={newOpt} onChange={(e) => setNewOpt(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOpt())}
                            placeholder="Nueva opción…"
                            data-testid="new-gfield-option-input"
                            className="flex-1 px-3 py-1.5 border-2 outline-none text-sm"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                        <button type="button" onClick={addOpt} data-testid="new-gfield-add-option"
                            className="px-3 py-1.5 text-white text-xs font-bold" style={{ background: ACCENT }}>Añadir</button>
                    </div>
                </div>
            )}

            <IndustryPicker
                label="Giros aplicables (vacío = todos)"
                value={form.applies_to_industries}
                options={industryOptions}
                onChange={(v) => setField({ applies_to_industries: v })}
                testId="new-gfield-industries"
            />

            <div className="flex gap-2 pt-4 border-t sticky bottom-0 -mx-5 -mb-5 px-5 py-3" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                <button onClick={submit} disabled={disabled} data-testid="new-gfield-submit"
                    className="flex-1 text-white px-5 py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: ACCENT }}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Crear campo
                </button>
                <button onClick={onClose} className="px-5 py-2.5 border text-sm font-medium" style={{ borderColor: 'var(--border)' }}>
                    Cancelar
                </button>
            </div>
        </Modal>
    );
}


function DeleteFieldConfirm({ field, usage, onConfirm, onClose }) {
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const matches = text.trim() === field.key;
    const submit = async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } };
    return (
        <Modal onClose={onClose} title={`Eliminar campo · ${field.key}`}>
            <div className="p-3 mb-3 border-l-4 text-sm" style={{ borderColor: '#DC2626', background: 'rgba(220,38,38,0.05)' }}>
                <div className="font-bold text-red-700 dark:text-red-500 mb-1">Acción permanente</div>
                {usage?.responses_count > 0 ? (
                    <p>
                        Este campo tiene <strong>{usage.responses_count} respuesta{usage.responses_count === 1 ? '' : 's'}</strong> capturada{usage.responses_count === 1 ? '' : 's'} en auditorías. Al eliminarlo, esos datos se archivarán en <code>general_data_archive.{field.key}</code> y dejarán de ser editables desde el formulario.
                    </p>
                ) : (
                    <p>Este campo no tiene respuestas capturadas. Se removerá del template sin datos que archivar.</p>
                )}
            </div>
            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">
                Para confirmar, escribe la clave exacta: <span className="font-mono text-red-600">{field.key}</span>
            </label>
            <input type="text" value={text} onChange={(e) => setText(e.target.value)}
                data-testid="delete-gfield-confirm-input"
                className="w-full px-3 py-2 border-2 outline-none text-sm mb-4 font-mono"
                style={{ borderColor: matches ? '#DC2626' : 'var(--border)', background: 'var(--card-bg)' }}
                placeholder={field.key} />
            <div className="flex gap-2 pt-4 border-t sticky bottom-0 -mx-5 -mb-5 px-5 py-3" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                <button onClick={submit} disabled={!matches || busy}
                    data-testid="delete-gfield-confirm"
                    className="flex-1 text-white px-5 py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40"
                    style={{ background: '#DC2626' }}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Eliminar campo definitivamente
                </button>
                <button onClick={onClose} className="px-5 py-2.5 border text-sm font-medium" style={{ borderColor: 'var(--border)' }}>
                    Cancelar
                </button>
            </div>
        </Modal>
    );
}
